// A one-line current state for the open Settings section (Session SC7B).
//
// The section header shows one quiet line (the backup headline, how many integrations are connected) taken
// from data a card ALREADY fetched for itself. There is no new request for it: a card that knows its own state
// reports it here, and a card rendered anywhere else (a test, another page) reports to nothing. When a card
// has no status line of its own, the section simply shows no summary.
import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";

type Report = (text: string | null) => void;

const SummaryContext = createContext<Report>(() => {});

export function SettingsSummaryProvider({ report, children }: { report: Report; children: ReactNode }) {
  return <SummaryContext.Provider value={report}>{children}</SummaryContext.Provider>;
}

/** Report a card's one-line state to the section header. Pass null until the card knows it. */
export function useReportSettingsSummary(text: string | null): void {
  const report = useContext(SummaryContext);
  // The reporter is read through a ref, so a provider that hands a new function every render cannot re-run this effect.
  const ref = useRef(report);
  ref.current = report;
  useEffect(() => {
    ref.current(text);
    return () => ref.current(null);
  }, [text]);
}
